import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UploadCloud, Download, FileSpreadsheet, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

function UploadCard({ onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/admin/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success(`Uploaded as ${data.detected_type.toUpperCase()} — ${data.records_inserted} records`);
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Card
      className="p-6 border-dashed hover:border-primary/60 transition-colors"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center">
          <UploadCloud className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div>
          <div className="font-heading font-semibold text-lg">Upload data file (auto-detect)</div>
          <div className="text-xs text-muted-foreground">CSV or Excel · GSTIN column → GST · entityId column → MCA · Each upload becomes a new daily dataset</div>
        </div>
      </div>
      <div className="mt-4 rounded-md border border-border p-6 text-center bg-muted/30">
        <div className="text-sm text-muted-foreground">Drop your file here or</div>
        <div className="mt-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="upload-input"
          />
          <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="upload-btn">
            {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Uploading… {progress}%</> : <>Choose file</>}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function FileList({ dtype, refreshKey }) {
  const [files, setFiles] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/files`, { params: { dtype } });
      setFiles(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }, [dtype]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const download = async (id, filename) => {
    try {
      const r = await fetch(`${API_BASE}/admin/file/${id}/download`, { credentials: "include" });
      if (!r.ok) throw new Error(`Download failed ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : filename;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const removeFile = async (id) => {
    try {
      const { data } = await api.delete(`/admin/files/${id}`);
      toast.success(`File deleted (${data.records_deleted} records removed)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <Card className="p-0 mt-6 overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="font-heading font-semibold">Uploaded {dtype.toUpperCase()} files</div>
        <div className="text-xs text-muted-foreground num">{files.length} file(s)</div>
      </div>
      {files.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">
          <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground/60" />
          <div className="mt-2">No {dtype.toUpperCase()} files uploaded yet.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2.5">Upload date</th>
              <th className="text-left px-4 py-2.5">Filename</th>
              <th className="text-right px-4 py-2.5">Records</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className="border-t border-border hover:bg-accent/40" data-testid={`file-row-${f.id}`}>
                <td className="px-4 py-2.5 num">{new Date(f.upload_date).toLocaleString()}</td>
                <td className="px-4 py-2.5">{f.filename}</td>
                <td className="px-4 py-2.5 text-right num font-medium">{f.record_count.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => download(f.id, f.filename)} data-testid={`file-download-${f.id}`}>
                      <Download className="h-4 w-4 mr-1.5" /> Download
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="destructive" data-testid={`file-delete-${f.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-popover">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {f.filename}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently remove the file and all {f.record_count.toLocaleString()} records inside it. Customers will no longer see this dataset.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeFile(f.id)} data-testid={`file-confirm-delete-${f.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Card>
  );
}

export default function AdminUploads() {
  const [tab, setTab] = useState("gst");
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl">Uploads</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">One uploader — the file's type is auto-detected from its column headers. Delete removes the file and all its records.</p>
      </div>

      <UploadCard onUploaded={() => setRefreshKey((k) => k + 1)} />

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="uploads-tabs">
          <TabsTrigger value="gst" data-testid="tab-gst">GST files</TabsTrigger>
          <TabsTrigger value="mca" data-testid="tab-mca">MCA files</TabsTrigger>
        </TabsList>
        <TabsContent value="gst" className="mt-0">
          <FileList dtype="gst" refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="mca" className="mt-0">
          <FileList dtype="mca" refreshKey={refreshKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
